import { FC, useCallback, useEffect, useState } from 'react';
import { NudosButton, NudosSelectButton } from '../../../../components/NudosComponents';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import BordSelectDropdown from '../../../../components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import {
  TEMPORARY_STORAGE_REASON_TYPE_ID,
  TEMPORARY_STORAGE_REASON_TYPE_NAME
} from '../../../../types/orders';
import './TransitToWarehouseReasonModal.scss';

interface ITransitToWarehouseReasonModalProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: (reasons: TEMPORARY_STORAGE_REASON_TYPE_ID[]) => void;
  /** Motivos preseleccionados (p. ej. edición desde tracking con datos de parada en bodega). */
  initialSelectedReasons?: TEMPORARY_STORAGE_REASON_TYPE_ID[];
}

const WAREHOUSE_STOP_REASON_OPTIONS = [
  { id: TEMPORARY_STORAGE_REASON_TYPE_ID.CONFIGURATION, name: TEMPORARY_STORAGE_REASON_TYPE_NAME.CONFIGURATION },
  { id: TEMPORARY_STORAGE_REASON_TYPE_ID.PREPARATION, name: TEMPORARY_STORAGE_REASON_TYPE_NAME.PREPARATION },
  { id: TEMPORARY_STORAGE_REASON_TYPE_ID.INVOICE, name: TEMPORARY_STORAGE_REASON_TYPE_NAME.INVOICE }
] as const;

/**
 * Modal para elegir motivos antes de pasar un envío de Preparación a Tránsito a bodega.
 */
const TransitToWarehouseReasonModal: FC<ITransitToWarehouseReasonModalProps> = ({
  setShowModal,
  onConfirm,
  initialSelectedReasons
}) => {
  const [selectedReasons, setSelectedReasons] = useState<TEMPORARY_STORAGE_REASON_TYPE_ID[]>(
    () => initialSelectedReasons ?? []
  );

  useEffect(() => {
    setSelectedReasons(initialSelectedReasons ?? []);
  }, [initialSelectedReasons]);

  const handleToggleReason = useCallback((event: React.MouseEvent, reasonId: TEMPORARY_STORAGE_REASON_TYPE_ID) => {
    event?.stopPropagation();
    setSelectedReasons(prev =>
      prev.includes(reasonId) ? prev.filter(id => id !== reasonId) : [...prev, reasonId]
    );
  }, []);

  const handleConfirm = () => {
    onConfirm(selectedReasons);
    setShowModal(false);
  };

  const handleClose = () => setShowModal(false);

  const dropdownDisplayValue =
    selectedReasons.length > 0
      ? WAREHOUSE_STOP_REASON_OPTIONS.filter(r => selectedReasons.includes(r.id))
          .map(r => r.name)
          .join(', ')
      : 'Motivo de parada en bodega';

  const selectOptions = WAREHOUSE_STOP_REASON_OPTIONS.map(reason => (
    <div
      key={reason.id}
      className="optionContainer transitToWarehouseReasonModalOptionItem"
      onClick={(e: React.MouseEvent<HTMLDivElement>) => handleToggleReason(e, reason.id)}
    >
      <NudosSelectButton
        componentSize="xs"
        handleClickButton={(e: React.MouseEvent<HTMLDivElement>) => handleToggleReason(e, reason.id)}
        isButtonActive={selectedReasons.includes(reason.id)}
      />
      <span className="transitToWarehouseReasonModalReasonName">{reason.name}</span>
    </div>
  ));

  return (
    <div id="transitToWarehouseReasonModal" className="transitToWarehouseReasonModal">
      <div className="transitToWarehouseReasonModalContainer">
        <div className="transitToWarehouseReasonModalFloatClose" onClick={handleClose}>
          <BordOneToneIcon variant="x" customWidth="16px" />
        </div>
        <div className="transitToWarehouseReasonModalBox">
          <div className="transitToWarehouseReasonModalHeader">
            <div className="transitToWarehouseReasonModalIconWrapper">
              <BordOneToneIcon variant="triangleAlertCircle" standardSize={32} customWidth="32px" />
            </div>
            <h2 className="transitToWarehouseReasonModalTitle">Indica el motivo de Tránsito a bodega</h2>
          </div>
          <div className="transitToWarehouseReasonModalReasonsSection">
            <BordSelectDropdown
              currentValueOrplaceholder={dropdownDisplayValue}
              isFilled={selectedReasons.length > 0}
              selectOptions={selectOptions}
              closesOnChangeSelection={false}
              selectClassName="transitToWarehouseReasonModalDropdown"
            />
          </div>
          <div className="transitToWarehouseReasonModalButtonContainer">
            <NudosButton
              colorPalette="secondary"
              buttonText="Cancelar"
              handleClick={handleClose}
            />
            <NudosButton
              colorPalette="primary"
              buttonText="Aceptar"
              handleClick={handleConfirm}
              isButtonDisabled={selectedReasons.length === 0}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransitToWarehouseReasonModal;
